import {
  ConfirmDialog,
} from "@common";
import {
  getCrmTeamApi,
  getProjectUser,
  rowSelectCrmTeam,
} from "@features";
import { Box } from "@mui/material";
import { KanbanDetailsContent } from "@pionglobal/pionsharedui";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CrmTeamEdit from "./CrmTeamEdit";
import useCrmTeamForm from "./hooks/useCrmTeamForm";
import { getCrmTeamView } from "./utils/crmTeamView";

const SEGMENT = {
  CRM_TEAM: "crm_team",
};

const sanitizeId = (id) => {
  if (id === true || id === false || id === "true" || id === "false" || id === undefined || id === null) {
    return null;
  }
  return id;
};

const getSelectedId = (data) => {
  if (Array.isArray(data)) {
    return sanitizeId(data[0]?.id) || null;
  }
  return sanitizeId(data?.id) || null;
};

const resolveTeamId = (propTeamId, campaignTeamId, selectedCrmTeam) =>
  propTeamId || campaignTeamId || getSelectedId(selectedCrmTeam);

const resolveRecord = (crmTeamData, selectedCrmTeam, teamId) => {
  if (Array.isArray(crmTeamData)) {
    const found = crmTeamData.find((item) => item?.id === teamId);
    if (found) return found;
  } else if (crmTeamData?.id === teamId) {
    return crmTeamData;
  }
  if (Array.isArray(selectedCrmTeam)) {
    return selectedCrmTeam[0] || {};
  }
  return selectedCrmTeam || {};
};

const updateSelectedTeamItem = (res, teamId, dispatch) => {
  const list = res?.payload?.data || res?.payload || [];
  if (!Array.isArray(list) || !teamId) return;
  const updatedItem = list.find((item) => item?.id === teamId);
  if (updatedItem) {
    dispatch(rowSelectCrmTeam([updatedItem]));
  }
};

export default function CrmTeamView({
  teamId: propTeamId,
  campaignTeamId,
  forCampaign,
  forProspect,
  forInquiry,
  forLead,
  forOpportunity,
  forAccount,
  forCustomer,
  forContact,
  forContract,
  forOrder,
  campaignId: propCampaignId,
  prospectId: propProspectId,
  inquiryId: propInquiryId,
  leadId: propLeadId,
  opportunityId: propOpportunityId,
  customerAccountId: propCustomerAccountId,
  customerId: propCustomerId,
  contactId: propContactId,
  contractId: propContractId,
  orderId: propOrderId,
  getFunction: parentGetFunction,
  ...props
}) {
  const dispatch = useDispatch();

  const campaignSelectedData = useSelector(
    (state) => state.campaign?.selectedData
  );
  const prospectSelectedData = useSelector(
    (state) => state.prospect?.selectedData
  );
  const inquirySelectedData = useSelector(
    (state) => state.inquiry?.selectedData
  );
  const leadSelectedData = useSelector(
    (state) => state.lead?.selectedData
  );
  const opportunitySelectedData = useSelector(
    (state) => state.opportunity?.selectedData
  );
  const accountSelectedData = useSelector(
    (state) => state.account?.selectedData
  );
  const customerSelectedData = useSelector(
    (state) => state.customer?.selectedData
  );
  const contactSelectedData = useSelector(
    (state) => state.contact?.selectedData
  );
  const contractSelectedData = useSelector(
    (state) => state.contract?.selectedData
  );
  const orderSelectedData = useSelector(
    (state) => state.order?.selectedData
  );

  const propsWithFlags = {
    forCampaign,
    forProspect,
    forInquiry,
    forLead,
    forOpportunity,
    forAccount,
    forCustomer,
    forContact,
    forContract,
    forOrder,
    campaignId: propCampaignId,
    prospectId: propProspectId,
    inquiryId: propInquiryId,
    leadId: propLeadId,
    opportunityId: propOpportunityId,
    customerAccountId: propCustomerAccountId,
    customerId: propCustomerId,
    contactId: propContactId,
    contractId: propContractId,
    orderId: propOrderId,
    ...props,
  };

  const api = useMemo(
    () => getCrmTeamApi(propsWithFlags),
    [
      forCampaign,
      forProspect,
      forInquiry,
      forLead,
      forOpportunity,
      forAccount,
      forCustomer,
      forContact,
      forContract,
      forOrder,
      propCampaignId,
      propProspectId,
      propInquiryId,
      propLeadId,
      propOpportunityId,
      propCustomerAccountId,
      propCustomerId,
      propContactId,
      propContractId,
      propOrderId,
      props?.entityType,
    ]
  );

  const activeEntityId = useMemo(() => {
    const propId = sanitizeId(propsWithFlags?.[api.idKey]);
    if (propId && (typeof propId === "string" || typeof propId === "number")) {
      return propId;
    }
    let resolved = null;
    switch (api.entityType) {
      case "contract":
        resolved = sanitizeId(propContractId) || getSelectedId(contractSelectedData);
        break;
      case "customer-account":
        resolved = sanitizeId(propCustomerAccountId) || getSelectedId(accountSelectedData);
        break;
      case "contact":
        resolved = sanitizeId(propContactId) || getSelectedId(contactSelectedData);
        break;
      case "opportunity":
        resolved = sanitizeId(propOpportunityId) || getSelectedId(opportunitySelectedData);
        break;
      case "lead":
        resolved = sanitizeId(propLeadId) || getSelectedId(leadSelectedData);
        break;
      case "campaign":
        resolved = sanitizeId(propCampaignId) || getSelectedId(campaignSelectedData);
        break;
      case "inquiry":
        resolved = sanitizeId(propInquiryId) || getSelectedId(inquirySelectedData);
        break;
      case "prospect":
        resolved = sanitizeId(propProspectId) || getSelectedId(prospectSelectedData);
        break;
      case "customer":
        resolved = sanitizeId(propCustomerId) || getSelectedId(customerSelectedData);
        break;
      case "order":
        resolved = sanitizeId(propOrderId) || getSelectedId(orderSelectedData);
        break;
      default:
        resolved = null;
    }
    return sanitizeId(resolved);
  }, [
    api.entityType,
    api.idKey,
    propsWithFlags,
    propContractId,
    propCustomerAccountId,
    propContactId,
    propOpportunityId,
    propLeadId,
    propCampaignId,
    propInquiryId,
    propProspectId,
    propCustomerId,
    propOrderId,
    contractSelectedData,
    accountSelectedData,
    contactSelectedData,
    opportunitySelectedData,
    leadSelectedData,
    campaignSelectedData,
    inquirySelectedData,
    prospectSelectedData,
    customerSelectedData,
    orderSelectedData,
  ]);

  const selectedCrmTeam = useSelector(
    (state) => state.crmTeam?.selectedData
  );
  const crmTeamState = useSelector((state) => state.crmTeam);
  const crmTeamData =
    crmTeamState?.details || crmTeamState?.data;
  const projectUsersOptions =
    useSelector((state) => state?.projectUsers?.data) || [];

  const loading = crmTeamState?.loading;

  const [editingSegment, setEditingSegment] = useState(null);

  const teamId = resolveTeamId(propTeamId, campaignTeamId, selectedCrmTeam);

  const record = useMemo(
    () => resolveRecord(crmTeamData, selectedCrmTeam, teamId),
    [crmTeamData, selectedCrmTeam, teamId]
  );

  const handleSuccess = useCallback(() => {
    setEditingSegment(null);
    const cleanEntityId = sanitizeId(activeEntityId);
    if (cleanEntityId) {
      dispatch(api.get({ [api.idKey]: cleanEntityId })).then((res) => {
        updateSelectedTeamItem(res, teamId, dispatch);
      });
    } else {
      dispatch(api.get()).then((res) => {
        updateSelectedTeamItem(res, teamId, dispatch);
      });
    }
    dispatch(getProjectUser());
    parentGetFunction?.();
  }, [dispatch, api, activeEntityId, teamId, parentGetFunction]);

  const crmTeamForm = useCrmTeamForm({
    forCampaign,
    forProspect,
    forInquiry,
    forLead,
    forOpportunity,
    forAccount,
    forCustomer,
    forContact,
    forContract,
    forOrder,
    entityType: api.entityType,
    ...(sanitizeId(activeEntityId) ? { [api.idKey]: sanitizeId(activeEntityId) } : {}),
    data: record,
    onSuccess: handleSuccess,
  });

  const crmTeamView = useMemo(
    () => getCrmTeamView(record, projectUsersOptions),
    [record, projectUsersOptions]
  );

  const canOpenSegment = useCallback(
    (segmentId) => editingSegment === null || editingSegment === segmentId,
    [editingSegment]
  );

  const openSegment = useCallback((segmentId, formik) => {
    formik?.resetForm?.();
    setEditingSegment(segmentId);
  }, []);

  const cancelSegment = useCallback((formik) => {
    formik?.resetForm?.();
    setEditingSegment(null);
  }, []);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  const handleDelete = useCallback(() => {
    const idToDelete = teamId || record?.id;
    if (!idToDelete) return;
    dispatch(api.delete({ id: idToDelete })).then((res) => {
      if (
        res?.payload?.ok ||
        res?.payload?.data?.ok ||
        res?.meta?.requestStatus === "fulfilled" ||
        !res?.error
      ) {
        setConfirmDialog({ isOpen: false, title: "", subTitle: "" });
        dispatch(rowSelectCrmTeam([]));
        const cleanEntityId = sanitizeId(activeEntityId);
        if (cleanEntityId) {
          dispatch(api.get({ [api.idKey]: cleanEntityId }));
        } else {
          dispatch(api.get());
        }
        dispatch(getProjectUser());
        parentGetFunction?.();
      }
    });
  }, [dispatch, api, teamId, record?.id, activeEntityId, parentGetFunction]);

  const handleDeleteClick = useCallback(() => {
    setConfirmDialog({
      isOpen: true,
      title: "Are you sure to delete this record?",
      subTitle: "You can't undo this operation",
      onConfirm: handleDelete,
    });
  }, [handleDelete]);

  const segments = useMemo(
    () => [
      {
        id: SEGMENT.CRM_TEAM,
        visible: true,
        gridSize: { xs: 12, sm: 12, md: 12, lg: 12 },
        title: props?.heading ? `${props?.heading} Details` : "CRM Team Details",
        left: crmTeamView.left,
        right: crmTeamView.right,
        fullWidth: crmTeamView.fullWidth,
        editable: canOpenSegment(SEGMENT.CRM_TEAM),
        isEditing: editingSegment === SEGMENT.CRM_TEAM,
        editContent: (
          <CrmTeamEdit
            formik={crmTeamForm.formik}
            options={crmTeamForm.options}
            disabled={loading || crmTeamForm.formik.isSubmitting}
          />
        ),
        onEdit: () => openSegment(SEGMENT.CRM_TEAM, crmTeamForm.formik),
        onSave: () => crmTeamForm.formik.submitForm(),
        onCancel: () => cancelSegment(crmTeamForm.formik),
        deleteAction: {
          visible: true,
          disabled: false,
          tooltip: "Delete CRM Team",
          onClick: handleDeleteClick,
        },
        close: {
          visible: true,
          disabled: false,
          onClick: () => {
            dispatch(rowSelectCrmTeam([]));
          },
        },
      },
    ],
    [
      crmTeamView,
      canOpenSegment,
      editingSegment,
      crmTeamForm,
      loading,
      openSegment,
      cancelSegment,
      handleDeleteClick,
      dispatch,
      props?.heading,
    ]
  );

  const { user_created, user_updated, createdOn, updatedOn } = record || {};

  return (
    <Box sx={{ width: "100%", p: 0 }}>
      <KanbanDetailsContent segments={segments} />

      {(user_created || user_updated || createdOn || updatedOn) && (
        <Box sx={{ px: 2, pt: 1, pb: 1 }}>
        </Box>
      )}

      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </Box>
  );
}
